import React from 'react';
import { useTranslation } from 'react-i18next';
import { apiDocStyles } from '../styles/apiDocStyles';
import Breadcrumb from './Breadcrumb';
import MethodBadge from './MethodBadge';
import FieldTable from './FieldTable';
import CodeBlock from './CodeBlock';
import EndpointCard from './EndpointCard';

const DocContent = ({
  apiData,
  selectedCategory,
  selectedEndpoint,
  onNavigate,
  isMobile
}) => {
  const { t } = useTranslation();

  const category = apiData.categories.find(c => c.id === selectedCategory);
  const endpoint = category?.endpoints.find(e => e.id === selectedEndpoint);

  const contentStyle = {
    ...apiDocStyles.contentWrapper,
    ...(isMobile && { padding: '20px 16px' }),
  };

  // Overview page (no category selected)
  if (!selectedCategory) {
    return (
      <div style={contentStyle}>
        <h1 style={apiDocStyles.pageTitle}>{t('apiDoc.title')}</h1>
        <p style={apiDocStyles.pageDescription}>
          {t('apiDoc.welcomeDesc')}
        </p>

        {/* API Categories Overview */}
        {apiData.categories.map(cat => (
          <div key={cat.id} style={apiDocStyles.section}>
            <h2 style={apiDocStyles.sectionTitle}>{cat.name}</h2>
            <p style={apiDocStyles.sectionContent}>{cat.description}</p>
            <div style={{ display: 'grid', gap: '12px', marginTop: '16px' }}>
              {cat.endpoints.map(ep => (
                <EndpointCard
                  key={ep.id}
                  endpoint={ep}
                  onClick={() => onNavigate(cat.id, ep.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Category page (no endpoint selected)
  if (!selectedEndpoint) {
    return (
      <div style={contentStyle}>
        <Breadcrumb
          category={category}
          endpoint={null}
          onNavigate={onNavigate}
        />

        <h1 style={apiDocStyles.pageTitle}>{category.name}</h1>
        <p style={apiDocStyles.pageDescription}>{category.description}</p>

        <div style={{ display: 'grid', gap: '12px' }}>
          {category.endpoints.map(ep => (
            <EndpointCard
              key={ep.id}
              endpoint={ep}
              onClick={() => onNavigate(category.id, ep.id)}
            />
          ))}
        </div>
      </div>
    );
  }

  // Endpoint detail page
  return (
    <div style={contentStyle}>
      <Breadcrumb
        category={category}
        endpoint={endpoint}
        onNavigate={onNavigate}
      />

      {/* Endpoint Title */}
      <h1 style={apiDocStyles.pageTitle} id="overview">{endpoint.name}</h1>
      <p style={apiDocStyles.pageDescription}>{endpoint.description}</p>

      {/* Endpoint URL */}
      <div style={apiDocStyles.endpointUrl}>
        <MethodBadge method={endpoint.method} />
        <span style={apiDocStyles.urlPath}>{endpoint.path}</span>
      </div>

      {/* Request Section */}
      <section style={apiDocStyles.section} id="request">
        <h2 style={apiDocStyles.sectionTitle}>{t('apiDoc.request')}</h2>

        {/* Request Headers */}
        {endpoint.requestHeaders && endpoint.requestHeaders.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              {t('apiDoc.headers')}
            </h3>
            <FieldTable fields={endpoint.requestHeaders} type="request" />
          </div>
        )}

        {/* Request Body */}
        {endpoint.requestBody && endpoint.requestBody.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              {t('apiDoc.requestBody')}
            </h3>
            <FieldTable fields={endpoint.requestBody} type="request" />
          </div>
        )}

        {/* Request Example */}
        {endpoint.requestExample && (
          <CodeBlock
            code={endpoint.requestExample}
            title={t('apiDoc.requestExample')}
          />
        )}
      </section>

      {/* Response Section */}
      <section style={apiDocStyles.section} id="response">
        <h2 style={apiDocStyles.sectionTitle}>{t('apiDoc.response')}</h2>

        {/* Response Fields */}
        {endpoint.responseFields && endpoint.responseFields.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              {t('apiDoc.responseFields')}
            </h3>
            <FieldTable fields={endpoint.responseFields} type="response" />
          </div>
        )}

        {/* Response Example */}
        {endpoint.responseExample && (
          <div id="example">
            <CodeBlock
              code={endpoint.responseExample}
              title={t('apiDoc.responseExample')}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default DocContent;
